/**
 * Module dependencies
 */
const React = require('react');
const { shape, string, bool, func, objectOf, any, number } = require('prop-types');
const { Script } = require('nordic/script');
const Style = require('nordic/style');
const injectI18n = require('nordic/i18n/injectI18n');

const Card = require('@andes/card');
const List = require('@andes/list');

const classnames = require('classnames');
const InputSearch = require('../../components/InputSearch');
const InputSearchV2 = require('../../components/InputSearchV2');
const ContactButtonV2 = require('../../components/ContactButtonV2');
const Effectivity = require('./components/Effectivity');

const { useUrlState } = require('../../utils/context/url');
const { useFlags } = require('../../utils/context/flags');
const { WEBVIEW_CUSTOM_PORTAL_VALID } = require('../../utils/constants');
const Icon = require('../../components/Icon');
const { isWebkit2, isValidVersion, executeShareEvent, createDeeplink } = require('../../utils/mobile/nativeActions');
const GoHomeButton = require('./components/GoHome');
const { Text } = require('@andes/typography');
const { useNavigationDetailsHandler } = require('../../hooks/useNavigationDetailsHandler');
const MHRealEstate = require('../../components/MHRealEstate');

const { ListItem } = List;
const { isWebMobile } = require('../../utils');

const addContentAccessibilityAttrs = (divElement) => {
  const elements = divElement.querySelectorAll('p, ul, ol, img, h2, h3, h4');

  elements.forEach((element) => {
    if (element.tagName === 'P') {
      element.setAttribute('role', 'paragraph');
    } else if (element.tagName === 'UL' || element.tagName === 'OL') {
      element.setAttribute('role', 'list');
    } else if (element.tagName === 'IMG') {
      element.setAttribute('role', 'img');
    } else if (element.tagName === 'H2' || element.tagName === 'H3' || element.tagName === 'H4') {
      element.setAttribute('role', 'heading');
    }
  });
};

const MARIO_NAVI_MIGRATION_DATE = '2025-03-21T11:00:00Z';
/**
 * Content Component
 */
const Content = ({
  i18n,
  actualLocation,
  device,
  styleFile,
  platform,
  appInfo,
  content,
  zrp,
  state,
  omniOneClickData,
  melichat,
  melichatUrlBase,
  realStateId,
  bugId,
  breadcrumbLevels,
  nativeAppNavigation,
  themeRebranding,
}) => {
  const { displayLevelTitle, levelTitle } = useNavigationDetailsHandler({ breadcrumbLevels, nativeAppNavigation });

  const onClickTracking = React.useCallback((destinationUrl, path = '/portal/faq/action_link') => {
    if (window && window.melidata) {
      window.melidata('cleanAndSend', 'event', {
        path,
        event_data: {
          portal_content_id: content.content_id,
          portal_content_destination_url: destinationUrl,
          portal_problem_id: content?.problem_id,
        },
      });
    }
  }, [content]);

  const contentContainer = React.useRef(null);

  React.useEffect(() => {
    if (contentContainer.current) {
      const links = contentContainer.current.querySelectorAll('a');
      links.forEach((link) => {
        link.addEventListener('click', () => onClickTracking(link.href, '/portal/faq/internal_links'));
      });
    }
  }, [contentContainer, onClickTracking]);

  const { id: business } = platform;
  const { baseUrl, homeUrl, hostname, headers = {} } = useUrlState();
  const isYoshi = headers['x-webkit-custom'];
  const isWallet = hostname.includes('wallet');
  const { 'new-search': newSearch, 'new-search-mp': newSearchMP, 'cx-mobile-share-faqs': shareBtn, 'messages-hub-portal': showMessagesHub } = useFlags();
  const { webView: isNativeMobile } = device;
  const sourceId = state?.page?.melidata?.event_data?.portal_source_id;
  const [shouldShowShareIcon, setShouldShowShareIcon] = React.useState(false);
  const showNewSearch = ['ML', 'MS'].includes(business)
    ? newSearch
    : newSearchMP;
  const domain = platform?.domain || null;
  const siteId = platform?.siteId;
  const categoryIdRealestate = `portal:${content?.content_id || ''}`;

  React.useEffect(() => {
    if (isNativeMobile && shareBtn && isWebkit2() && isValidVersion(device) && isYoshi) {
      setShouldShowShareIcon(true);
    }
  }, []);

  const renderActions = (actions) => {
    const actionTitle = i18n.gettext('¿Cómo quieres continuar?');
    const dateChanged = new Date(content.date_changed);
    const migrationDate = new Date(MARIO_NAVI_MIGRATION_DATE);

    if (dateChanged && dateChanged > migrationDate) {
      return null;
    }

    if (!actions) {
      return null;
    }
    return (
      <div className="cx-actions">
        <h1 className="cx-peach-content__title">{actionTitle}</h1>
        <Card paddingSize={0} className="cx-actions__card">
          <List srLabel="content-actions-list" withDividers>
            {actions?.map(action => {
              const actionProps = getActionPropsToRedirect(action);
              return (
                <ListItem
                  key={actionProps.href}
                  title={actionProps.title}
                  href={actionProps.href}
                  target={actionProps.target}
                  rel={actionProps.rel}
                  className="cx-actions__item"
                  chevron
                  onClick={() => onClickTracking(actionProps.href)}
                />
              );
            })}
          </List>
        </Card>
      </div>
    );
  };

  const renderSearch = () => {
    const displayInputSearch = !isNativeMobile && showNewSearch && !zrp && !isWallet;
    if (!displayInputSearch) return null;
    if (isWebMobile(device)) {
      return renderSearchV2();
    }

    return (
      <div className="cx-search__input--container">
        <InputSearch />
      </div>
    );
  };

  const renderSearchV2 = () => (
    <section className="search-box-container">
      <InputSearchV2 />
    </section>
  );

  const getActionPropsToRedirect = (action) => {
    const actionProps = { title: action.label };
    const queryParamsMap = {};
    if (bugId) {
      queryParamsMap.bug_id = bugId;
    }
    const joinedQueryParams = Object.keys(queryParamsMap).reduce(
      (acc, key) => ([...acc, `${key}=${queryParamsMap[key]}`]),
      [],
    ).join(',');
    const queryParams = joinedQueryParams ? `?${joinedQueryParams}` : '';

    switch (action.type) {
      case 'CONTENT': {
        actionProps.rel = 'canonical';
        actionProps.target = '_self';
        actionProps.href = `${baseUrl}/${action.content_id}${queryParams}`;
        break;
      }
      case 'REDIRECT': {
        actionProps.target = '_parent';
        actionProps.href = action.url;
        break;
      }
      case 'CONTACT': {
        let url = `${baseUrl}/content/${content.content_id}/contact/${action.channels.problem_id}${queryParams}`;
        if (appInfo && appInfo.appParams) {
          const queryString = Object.entries(appInfo.appParams)
            .filter(([, value]) => value)
            .map(([key, value]) => `${key}=${value}`)
            .join('&');
          url += url.includes('?') ? `&${queryString}` : `?${queryString}`;
        }
        actionProps.target = '_self';
        actionProps.href = url;
        break;
      }
      default:
        break;
    }

    const isCustomPortalApp = baseUrl && WEBVIEW_CUSTOM_PORTAL_VALID.find(x => baseUrl.includes(x.prefix));
    if (isNativeMobile && (isCustomPortalApp || isWebkit2())) {
      const { id, domain } = platform;
      actionProps.href = isCustomPortalApp ? createDeeplink(actionProps.href, homeUrl, id) : createDeeplink(actionProps.href, `https://www.${domain}`, id);
    }
    return actionProps;
  };

  const setContent = contentToRender => ({ __html: contentToRender });

  const effectivityEventData = {
    portal_content_id: content.content_id,
    portal_has_channels_configured: !!content.actions?.length,
  };

  React.useEffect(() => {
    const contentElement = document.querySelector('.cx-peach-content__show');
    addContentAccessibilityAttrs(contentElement);
  }, []);
  return (
    <div className={classnames(`cx-peach-content cx-main--${platform.siteId}`, {
      'cx-peach--landing': content.is_landing,
    })}
    >
      <Style href={styleFile} critical={device.type === 'mobile'} />
      <Script src="vendor.js" />
      <Script src={`content.${device.type === 'mobile' ? 'mobile' : 'desktop'}.js`} />
      <Script>
        {`
          var agentKey = '_perfill';
          window[agentKey] = window[agentKey] || function (...args) {
            (window[agentKey].q = window[agentKey].q || []).push(args);
          };
          var perfillAgent = window[agentKey];
          perfillAgent('queue', 'start-recording', {
            traceability_id: 'content_id-${content.content_id}',
          });
        `}
      </Script>
      <>
        {renderSearch()}
        {displayLevelTitle && <Text component="p" size="s" color="secondary" className="cx-peach-content__level-title">
          {levelTitle}
        </Text>}
        <div className="cx-peach-content__title-container">
          <h1 className="cx-peach-content__title">
            {content.title}
          </h1>
          {shouldShowShareIcon && (
            <button
              className="cx-peach-content__share-button"
              onClick={() => executeShareEvent('cx_mobile_share', {
                title: content.title,
                url: actualLocation,
                portal_has_channels_configured: !!content.actions?.length,
                portal_content_id: content.content_id,
              })}
              aria-label="compartir"
            >
              <Icon type="share-icon" />
            </button>
          )}
        </div>
        {showMessagesHub && <div className="messages-hub-realestate-content"><MHRealEstate bu={business} categoryId={categoryIdRealestate} domain={domain} scope={'prod'} siteId={siteId} realestateId={realStateId} /></div>}
        <div className={classnames('cx-peach-content__data', {
          content__landing: content.is_landing,
        })}
        >
          <Card className="cx-peach-content__card">
            <div ref={contentContainer} className="cx-peach-content__show" dangerouslySetInnerHTML={setContent(content.content)} />
          </Card>
        </div>
      </>
      {renderActions(content.actions)}
      {content.allow_effectivity
        && (
          <Effectivity
            contentId={content.content_id}
            eventData={effectivityEventData}
            business={platform.id}
            device={device}
            sourceId={sourceId}
            theme={themeRebranding}
          />
        )}
      {omniOneClickData && omniOneClickData.showButton
        && <ContactButtonV2 omniOneClickButton chat={melichat} chatUrlBase={melichatUrlBase} flow={platform.id === 'MP' ? 'AA_MP_HOME' : 'AA_ML'} />}
      {isNativeMobile && <GoHomeButton platformId={platform?.id} />}
    </div>
  );
};

Content.propTypes = {
  device: shape({
    type: string.isRequired,
    webView: bool.isRequired,
  }).isRequired,
  styleFile: string.isRequired,
  i18n: shape({
    gettext: func.isRequired,
  }).isRequired,
  content: objectOf(any).isRequired,
  vote: number,
  hasVoted: bool,
  platform: shape({
    siteId: string,
  }).isRequired,
  appInfo: objectOf(any),
  zrp: objectOf(any),
  token: string,
  omniOneClickData: shape({
    showButton: bool,
  }),
  melichat: string,
  melichatUrlBase: string,
  bugId: string,
  furyConfig: objectOf(any),
};

Content.defaultProps = {
  vote: 1,
  hasVoted: false,
  appInfo: {},
  zrp: undefined,
  token: '',
  omniOneClickData: {
    showButton: false,
  },
  melichat: undefined,
  melichatUrlBase: string,
  bugId: '',
};

module.exports = injectI18n(Content);
